import { QWERTY, QWERTY_STROKE_OF } from './keyboardGeometry';

/**
 * Bijoy — the Bangla layout of Bangladesh's national standard (BDS 1738:2018) and of
 * every government typing test. The tool types it itself: it reads which physical
 * key went down (US-QWERTY name: `j`, `J`, `\`…) and turns the key sequence into
 * Unicode, so a learner needs no Bijoy installed.
 *
 * Key map from `bn-bijoyUnicode.mim` (nazdridoy/bijoyLinux, a modified copy of
 * Ananda Computers' own files). What makes Bijoy more than a lookup table:
 *  - ি ে ৈ are typed BEFORE the consonant cluster they follow on screen: কি = d j.
 *  - ো = c + cluster + f and ৌ = c + cluster + X (the two halves of the split vowel).
 *  - g is the link (্): C g C makes a conjunct, g + a kar key makes the independent
 *    vowel (g f = আ), g g leaves a visible hasanta.
 *  - Shift+A (ref, র্) is typed AFTER the consonant it sits on: কর্ম = j m A.
 *  - z = র-ফলা, Shift+Z = য-ফলা; র + য-ফলা gets a ZWJ so it shows as র‍্য, not র্য.
 * A port of the round-trip-tested prototype (45,748 of 45,757 words survive
 * encode → decode; the misses are misspelt source words).
 */

const NUKTA = '়';
const HAS = '্';
const ZWJ = '‍';
const ZWNJ = '‌';

/** key → consonant (nukta letters in their NFC, decomposed form) */
const CONS = {
    j: 'ক', J: 'খ', o: 'গ', O: 'ঘ', q: 'ঙ',
    y: 'চ', Y: 'ছ', u: 'জ', U: 'ঝ', I: 'ঞ',
    t: 'ট', T: 'ঠ', e: 'ড', E: 'ঢ', B: 'ণ',
    k: 'ত', K: 'থ', l: 'দ', L: 'ধ', b: 'ন',
    r: 'প', R: 'ফ', h: 'ব', H: 'ভ', m: 'ম',
    w: 'য', v: 'র', V: 'ল', M: 'শ', N: 'ষ', n: 'স', i: 'হ',
    p: `ড${NUKTA}`, P: `ঢ${NUKTA}`, W: `য${NUKTA}`,
};
const PREKAR = { d: 'ি', c: 'ে', C: 'ৈ' };
const POSTKAR = { f: 'া', D: 'ী', s: 'ু', S: 'ূ', a: 'ৃ', X: 'ৌ' };
/** g + key → independent vowel */
const INDEP = { f: 'আ', d: 'ই', D: 'ঈ', s: 'উ', S: 'ঊ', a: 'ঋ', c: 'এ', C: 'ঐ', x: 'ও', X: 'ঔ', G: '॥' };
const OTHER = {
    F: 'অ', x: 'ও', Q: 'ং', '|': 'ঃ', '&': 'ঁ', '\\': 'ৎ', G: '।', $: '৳',
    '`': '‘', "'": '’', '~': '“', '"': '”', Z: `${HAS}য`, z: `${HAS}র`, A: `র${HAS}`,
};
'০১২৩৪৫৬৭৮৯'.split('').forEach((digit, i) => { OTHER[String(i)] = digit; });

const isCons = (ch) => !!ch && ((ch >= 'ক' && ch <= 'হ') || ch === 'ড়' || ch === 'ঢ়' || ch === 'য়');
const endsWithBareRa = (s) => s.endsWith('র') && !s.endsWith(`${HAS}র`);

/** Index where the trailing consonant cluster C(্C)* of `s` begins, or -1. */
function clusterStart(s) {
    let i = s.length;
    if (i && s[i - 1] === NUKTA) i -= 1;
    if (!i || !isCons(s[i - 1])) return -1;
    i -= 1;
    while (i >= 2 && s[i - 1] === HAS && isCons(s[i - 2])) i -= 2;
    return i;
}

/** Bijoy key characters → Unicode (NFC). */
export function decodeBijoyKeys(keys) {
    let out = '';
    let pend = null; // pending pre-base kar
    let clus = ''; // the cluster being built after it
    const flush = () => {
        if (pend === null) return;
        out += clus ? clus + pend : pend;
        pend = null;
        clus = '';
    };
    let i = 0;
    while (i < keys.length) {
        const k = keys[i];
        if (pend !== null) {
            if (clus === '') {
                if (CONS[k]) { clus = CONS[k]; i += 1; continue; }
                flush();
                continue;
            }
            if (k === 'g' && CONS[keys[i + 1]]) { clus += HAS + CONS[keys[i + 1]]; i += 2; continue; }
            if (k === 'z') { clus += `${HAS}র`; i += 1; continue; }
            if (k === 'Z') { clus += (endsWithBareRa(clus) ? ZWJ : '') + HAS + 'য'; i += 1; continue; }
            if (k === 'A') { clus = `র${HAS}${clus}`; i += 1; continue; }
            if (k === 'f' && pend === 'ে') { out += `${clus}ো`; pend = null; clus = ''; i += 1; continue; }
            if (k === 'X' && pend === 'ে') { out += `${clus}ৌ`; pend = null; clus = ''; i += 1; continue; }
            flush();
            continue;
        }
        if (PREKAR[k]) { pend = PREKAR[k]; clus = ''; i += 1; continue; }
        if (k === 'g') {
            const next = keys[i + 1];
            if (next && INDEP[next]) { out += INDEP[next]; i += 2; continue; }
            if (next === 'g') { out += HAS + ZWNJ; i += 2; continue; }
            out += HAS;
            i += 1;
            continue;
        }
        if (k === 'A') {
            const start = clusterStart(out);
            out = start < 0 ? `${out}র${HAS}` : `${out.slice(0, start)}র${HAS}${out.slice(start)}`;
            i += 1;
            continue;
        }
        if (k === 'Z' && endsWithBareRa(out)) { out += `${ZWJ}${HAS}য`; i += 1; continue; }
        if (k === 'f' && out.endsWith('ে')) { out = `${out.slice(0, -1)}ো`; i += 1; continue; }
        if (k === 'X' && out.endsWith('ে')) { out = `${out.slice(0, -1)}ৌ`; i += 1; continue; }
        if (CONS[k]) out += CONS[k];
        else if (POSTKAR[k]) out += POSTKAR[k];
        else if (OTHER[k] !== undefined) out += OTHER[k];
        else out += k;
        i += 1;
    }
    flush();
    return out.normalize('NFC');
}

/* ---------------------------------------------------------------- encoder */

const REV_CONS = Object.fromEntries(Object.entries(CONS).map(([k, v]) => [v, k]));
const REV_INDEP = { আ: 'gf', ই: 'gd', ঈ: 'gD', উ: 'gs', ঊ: 'gS', ঋ: 'ga', এ: 'gc', ঐ: 'gC', ও: 'x', ঔ: 'gX', অ: 'F' };
const REV_SIMPLE = { 'ং': 'Q', 'ঃ': '|', 'ঁ': '&', 'ৎ': '\\', '।': 'G', '॥': 'gG', '৳': '$', '‘': '`', '’': "'", '“': '~', '”': '"' };
'০১২৩৪৫৬৭৮৯'.split('').forEach((digit, i) => { REV_SIMPLE[digit] = String(i); });
const KARS = 'ািীুূৃেৈোৌ';
const PRE_KEYS = { 'ি': 'd', 'ে': 'c', 'ৈ': 'C', 'ো': 'c', 'ৌ': 'c' };
const POST_KEYS = { 'া': 'f', 'ী': 'D', 'ু': 's', 'ূ': 'S', 'ৃ': 'a', 'ো': 'f', 'ৌ': 'X' };

function readCons(s, i) {
    if (s[i + 1] === NUKTA) return [s[i] + NUKTA, i + 2];
    return [s[i], i + 1];
}

/**
 * Unicode → syllables, each with the Bijoy keys that type it in classic order.
 * @returns {{ text: string, keys: string }[]}
 */
export function encodeBijoySyllables(text) {
    const s = String(text || '').normalize('NFC');
    const out = [];
    const n = s.length;
    let i = 0;
    while (i < n) {
        const from = i;
        const ch = s[i];
        if (isCons(ch)) {
            let ref = false;
            // ref র্ + consonant (but not র‍্য, which carries a ZWJ)
            if (ch === 'র' && s[i + 1] === HAS && isCons(s[i + 2])) { ref = true; i += 2; }
            let c;
            [c, i] = readCons(s, i);
            const base = [REV_CONS[c] || '?'];
            const phala = [];
            while ((s[i] === HAS && isCons(s[i + 1])) || (s[i] === ZWJ && s[i + 1] === HAS)) {
                if (s[i] === ZWJ) i += 1;
                const [c2, j] = readCons(s, i + 1);
                if (c2 === 'য') phala.push('Z');
                else if (c2 === 'র') phala.push('z');
                else if (phala.length) phala.push(`g${REV_CONS[c2] || '?'}`);
                else base.push(`g${REV_CONS[c2] || '?'}`);
                i = j;
            }
            const kar = KARS.includes(s[i]) ? s[i] : null;
            if (kar) i += 1;
            let keys = '';
            if (kar && PRE_KEYS[kar]) keys += PRE_KEYS[kar];
            keys += base.join('');
            if (ref) keys += 'A';
            keys += phala.join('');
            if (kar && POST_KEYS[kar]) keys += POST_KEYS[kar];
            if (s[i] === HAS) {
                const visible = s[i + 1] === ZWNJ;
                keys += visible ? 'gg' : 'g';
                i += visible ? 2 : 1;
            }
            out.push({ text: s.slice(from, i), keys });
            continue;
        }
        if (REV_INDEP[ch]) { out.push({ text: ch, keys: REV_INDEP[ch] }); i += 1; continue; }
        if (REV_SIMPLE[ch]) { out.push({ text: ch, keys: REV_SIMPLE[ch] }); i += 1; continue; }
        if (KARS.includes(ch)) { out.push({ text: ch, keys: POST_KEYS[ch] || PRE_KEYS[ch] }); i += 1; continue; }
        if (ch === ZWJ || ch === ZWNJ) { i += 1; continue; }
        out.push({ text: ch, keys: ch });
        i += 1;
    }
    return out;
}

export const encodeBijoy = (text) => encodeBijoySyllables(text).map((syl) => syl.keys).join('');

/* ----------------------------------------------------------- layout object */

/** Bijoy key character (`j`, `J`, `|`) ↔ physical stroke. */
const strokeOfKey = (key) => QWERTY_STROKE_OF[key] || null;
const keyOfStroke = (stroke) => {
    const pair = QWERTY[stroke.code];
    return pair ? pair[stroke.shift ? 1 : 0] : '';
};

/**
 * Equivalent ways to type a syllable a learner may know: ও also as g x, য-ফলা as
 * g w, র-ফলা as g v (Bijoy gives the same Unicode for each). The first spelling is
 * the canonical one the guide shows.
 */
function keyVariants(keys) {
    let variants = [keys];
    const swaps = [['Z', 'gw'], ['z', 'gv']];
    swaps.forEach(([from, to]) => {
        if (!keys.includes(from)) return;
        variants = variants.flatMap((v) => [v, v.split(from).join(to)]);
    });
    if (keys === 'x') variants.push('gx');
    return Array.from(new Set(variants)).slice(0, 8);
}

/** What each key prints in Bijoy — the on-screen keyboard legend. */
const BIJOY_LEGEND = (() => {
    const legend = {};
    Object.entries(QWERTY).forEach(([code, [plain, shifted]]) => {
        const show = (key) => {
            if (key === ' ') return '';
            if (CONS[key]) return CONS[key].normalize('NFC');
            if (PREKAR[key]) return PREKAR[key];
            if (POSTKAR[key]) return POSTKAR[key];
            if (key === 'g') return HAS;
            if (key === 'z') return '্র';
            if (key === 'Z') return '্য';
            if (key === 'A') return 'র্';
            if (OTHER[key] !== undefined) return OTHER[key];
            return key;
        };
        legend[code] = { plain: show(plain), shift: show(shifted) };
    });
    return legend;
})();

/** ASCII a Bangla passage may still carry, folded to what Bijoy types. */
function normalizeBangla(text) {
    return String(text || '')
        .normalize('NFC')
        .replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[Number(d)])
        .replace(/'/g, '’')
        .replace(/[“”"]/g, '')
        .replace(/[–—]/g, '-')
        .replace(/ /g, ' ');
}

/** A combining mark shows on a dotted circle when it stands alone (◌ি). */
export const withDottedBase = (text) => (/^[ঁ-ঃ়া-্ৗ]/.test(text) ? `◌${text}` : text);

export const bijoyLayout = {
    id: 'bijoy',
    script: 'bengali',
    name: 'Bangla (Bijoy)',
    nameBn: 'বাংলা (বিজয়)',
    legend: (code) => BIJOY_LEGEND[code] || null,
    normalizeText: normalizeBangla,
    encodeWord(word) {
        return encodeBijoySyllables(normalizeBangla(word)).map((syl) => ({
            text: syl.text,
            options: keyVariants(syl.keys)
                .map((keys) => Array.from(keys).map(strokeOfKey))
                .filter((strokes) => strokes.every(Boolean)),
        })).filter((unit) => unit.options.length);
    },
    render(strokes) {
        return decodeBijoyKeys(strokes.map(keyOfStroke).join(''));
    },
    strokeLabel(stroke) {
        if (!stroke) return '';
        if (stroke.code === 'Space') return '␣';
        const entry = BIJOY_LEGEND[stroke.code];
        if (!entry) return '';
        return withDottedBase(stroke.shift ? entry.shift : entry.plain);
    },
    statKey(stroke) {
        if (!stroke || stroke.code === 'Space') return null;
        const entry = BIJOY_LEGEND[stroke.code];
        if (!entry) return null;
        return stroke.shift ? entry.shift : entry.plain;
    },
};
